/**
 * useUnsavedChanges Hook
 *
 * Tracks unsaved changes and warns user before leaving the page.
 * Used for critical fields that require explicit save action.
 */

'use client';

import { useEffect, useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

interface UseUnsavedChangesOptions<T> {
  /** Initial/saved values to compare against */
  initialValues: T;
  /** Current form values */
  currentValues: T;
  /** Custom comparison function (defaults to JSON.stringify comparison) */
  compare?: (initial: T, current: T) => boolean;
  /** Whether to enable browser beforeunload warning */
  enableBrowserWarning?: boolean;
  /** Callback when user tries to navigate away with unsaved changes */
  onNavigationAttempt?: () => void;
}

interface UseUnsavedChangesReturn {
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
  /** Reset the initial values (call after successful save) */
  resetChanges: () => void;
  /** Mark current values as saved */
  markAsSaved: () => void;
  /** Whether user is attempting to navigate away */
  isNavigating: boolean;
  /** Confirm navigation (allow leaving) */
  confirmNavigation: () => void;
  /** Cancel navigation (stay on page) */
  cancelNavigation: () => void;
  /** Pending navigation path (if any) */
  pendingPath: string | null;
}

export function useUnsavedChanges<T>({
  initialValues,
  currentValues,
  compare,
  enableBrowserWarning = true,
  onNavigationAttempt,
}: UseUnsavedChangesOptions<T>): UseUnsavedChangesReturn {
  const router = useRouter();
  const savedValuesRef = useRef<T>(initialValues);
  const [isNavigating, setIsNavigating] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  // Default comparison using JSON.stringify
  const defaultCompare = useCallback((initial: T, current: T): boolean => {
    return JSON.stringify(initial) === JSON.stringify(current);
  }, []);

  const compareValues = compare ?? defaultCompare;

  const hasUnsavedChanges = !compareValues(savedValuesRef.current, currentValues);

  // Update saved values ref when initial values change (e.g., data loaded from server)
  useEffect(() => {
    savedValuesRef.current = initialValues;
  }, [initialValues]);

  // Browser beforeunload warning
  useEffect(() => {
    if (!enableBrowserWarning || !hasUnsavedChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [enableBrowserWarning, hasUnsavedChanges]);

  // Intercept Next.js navigation
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleRouteChange = (url: string) => {
      if (hasUnsavedChanges) {
        setIsNavigating(true);
        setPendingPath(url);
        onNavigationAttempt?.();
        // Prevent navigation by throwing (Next.js pattern)
        throw new Error('Navigation cancelled due to unsaved changes');
      }
    };

    // Note: Next.js App Router doesn't have built-in route change events
    // This is handled via the Link/router interception in the component
    return () => {};
  }, [hasUnsavedChanges, onNavigationAttempt]);

  const resetChanges = useCallback(() => {
    savedValuesRef.current = initialValues;
  }, [initialValues]);

  const markAsSaved = useCallback(() => {
    savedValuesRef.current = currentValues;
  }, [currentValues]);

  const confirmNavigation = useCallback(() => {
    setIsNavigating(false);
    if (pendingPath) {
      router.push(pendingPath);
      setPendingPath(null);
    }
  }, [pendingPath, router]);

  const cancelNavigation = useCallback(() => {
    setIsNavigating(false);
    setPendingPath(null);
  }, []);

  return {
    hasUnsavedChanges,
    resetChanges,
    markAsSaved,
    isNavigating,
    confirmNavigation,
    cancelNavigation,
    pendingPath,
  };
}
